/**
 * 예산 편성(요구/조정) 화면 공통 크롬
 * - Tree / Grid 패널 · 툴바 · 토글 — 화면별 styled 복제 금지
 * - 색·간격은 theme / CSS 변수(--erp-*)만 사용
 */
import styled from 'styled-components';
import { Card } from 'styles/common.styles';
import { theme, cssVar } from 'styles/theme';
import { hoverExpandScrollbarCss } from 'styles/scrollbar.styles';

export const TreePanel = styled(Card)`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding: 0;
  overflow: hidden;
`;

export const PanelHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  padding: 10px 12px;
  border-bottom: 1px solid ${cssVar('color-border', theme.colors.border)};
  font-weight: 700;
  font-size: ${theme.typography.fontSize.sm};
`;

export const TreeScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-x: auto;
  overflow-y: auto;
  padding: 4px 4px 12px 0;
  ${hoverExpandScrollbarCss}
`;

export const TreeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  width: max-content;
  min-width: 100%;
  box-sizing: border-box;
  padding: 2px 6px 2px ${(p) => 2 + p.$depth * 12}px;
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  border-radius: ${cssVar('radius-sm', theme.borderRadius.sm)};
  background: ${(p) =>
    p.$active ? cssVar('color-primary-light', theme.colors.primaryLight) : 'transparent'};
  color: ${(p) =>
    p.$active
      ? cssVar('color-primary', theme.colors.primary)
      : cssVar('color-text', theme.colors.text.primary)};
  user-select: none;
  &:hover {
    background: ${cssVar('color-primary-light', theme.colors.primaryLight)};
  }
`;

export const ToggleBtn = styled.button`
  flex: 0 0 ${theme.components.treeToggle.size};
  width: ${theme.components.treeToggle.size};
  height: ${theme.components.treeToggle.size};
  padding: 0;
  border: 1px solid ${cssVar('color-septenary', theme.colors.septenary)};
  border-radius: ${theme.borderRadius.xs};
  background: ${cssVar('color-bg-white', theme.colors.white)};
  color: ${cssVar('color-denary', theme.colors.denary)};
  font-size: ${theme.components.treeToggle.fontSize};
  line-height: 1;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const Caret = styled.span`
  flex: 0 0 10px;
  color: ${cssVar('color-green-strong', theme.colors.greenStrong)};
  font-size: ${theme.components.treeToggle.fontSize};
  text-align: center;
`;

export const LeafDot = styled.span`
  flex: 0 0 12px;
  text-align: center;
  color: ${cssVar('color-septenary', theme.colors.septenary)};
  font-size: ${theme.components.treeToggle.fontSize};
`;

export const TreeLabel = styled.span`
  flex: 0 0 auto;
  white-space: nowrap;
`;

export const RightStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  flex: 1;
  min-height: 0;
  height: 100%;
`;

export const GridPanel = styled(Card)`
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0;
  overflow: hidden;
  flex: ${(p) => p.$flex || 1.2};
`;

export const GridBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

export const Meta = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${cssVar('color-text-secondary', theme.colors.text.secondary)};
  margin-bottom: ${theme.spacing.xxxxs};
  flex-shrink: 0;
`;

export const TreeHeadActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xxxxs};
  flex-shrink: 0;
`;

export const TreeToggleBtn = styled.button`
  padding: 2px 8px;
  min-height: 22px;
  border: 1px solid
    ${(p) =>
      p.$active
        ? cssVar('color-primary', theme.colors.primary)
        : cssVar('color-border', theme.colors.border)};
  border-radius: ${cssVar('radius-sm', theme.borderRadius.sm)};
  background: ${(p) =>
    p.$active
      ? cssVar('color-primary-light', theme.colors.primaryLight)
      : cssVar('color-bg-white', theme.colors.white)};
  color: ${(p) =>
    p.$active
      ? cssVar('color-primary', theme.colors.primary)
      : cssVar('color-text-secondary', theme.colors.text.secondary)};
  font-size: ${theme.typography.fontSize.xxs};
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  cursor: pointer;
  line-height: 1.2;
  &:hover {
    border-color: ${cssVar('color-primary', theme.colors.primary)};
    color: ${cssVar('color-primary', theme.colors.primary)};
  }
`;

/** 산출내역 그리드 전용 툴바 — 공통 ActionButton 사이즈 유지 */
export const DetailToolbar = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  align-items: center;
  gap: ${theme.spacing.xxxxs};
  padding: ${theme.spacing.xxxxs} ${theme.spacing.xs};
  border-bottom: 1px solid ${cssVar('color-border', theme.colors.border)};
  background: ${cssVar('color-bg-toolbar', theme.colors.background.septenary)};
`;

/** 입력마감 등 상태 라벨 색 (토큰만) */
export function inputAvailStatusColor(label) {
  switch (label) {
    case '확정':
    case '마감일 초과':
      return theme.colors.status.dangerText;
    case '제출':
    case '적용일 미도래':
      return theme.colors.status.warningStrong;
    case '입력가능':
      return theme.colors.status.successStrong;
    default:
      return theme.colors.text.secondary;
  }
}
